// src/pages/FlashVendas.jsx
import React, { useEffect, useState, useRef, memo, forwardRef } from 'react';
import Menu from "../../comum/Menu/index.jsx";
import Footer from "../../comum/Footer/index.jsx";
import { useNavigate } from "react-router-dom";
import { api } from '../../services/api.js';
import Swal from 'sweetalert2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './flash.css';

// Registrar ChartJS
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  ArcElement
);

// Helpers
const getRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};
const generateUniqueColors = (count) => {
  const colors = [];
  const generated = new Set();
  while (colors.length < count) {
    const c = getRandomColor();
    if (!generated.has(c)) { colors.push(c); generated.add(c); }
  }
  return colors;
};
const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 }).format(value ?? 0);

// Loading modal
function LoadingModal({ show, message }) {
  if (!show) return null;
  return (
    <div className="loading-modal-overlay">
      <div className="loading-modal-content">
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
        <div style={{ marginTop: 15, fontSize: 18 }}>{message}</div>
      </div>
      <style>{`
        .loading-modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1050;
        }
        .loading-modal-content {
          background: white;
          padding: 20px 40px;
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          align-items: center;
          box-shadow: 0 0 10px rgba(0,0,0,0.25);
        }
      `}</style>
    </div>
  );
}

// Floating clock
const FloatingClock = ({ seconds }) => {
  const formatTime = (secs) => {
    const minutes = Math.floor(secs / 60);
    const remaining = secs % 60;
    return `${minutes.toString().padStart(2, '0')}:${remaining.toString().padStart(2, '0')}`;
  };
  return (
    <div className="floating-clock">
      <div className="clock-content">
        <span>Atualiza em:</span>
        <span className="countdown">{formatTime(seconds)}</span>
      </div>
      <style>{`
        .floating-clock { position: fixed; top: 70px; right: 10px; background: rgba(255,255,255,0.85); border-radius: 8px; padding: 8px 12px; z-index: 999; box-shadow: 0 2px 8px rgba(0,0,0,0.12); }
        .clock-content { display:flex; flex-direction:column; align-items:center; font-size:13px; }
        .countdown { font-weight:700; margin-top:4px; }
      `}</style>
    </div>
  );
};

// Memoized components
const MemoizedBar = memo(forwardRef(({ options, data, onClick }, ref) => (
  <Bar ref={ref} options={options} data={data} onClick={null} />
)));
const MemoizedDoughnut = memo(({ data, options }) => <Doughnut data={data} options={options} />);

// SecaoGrafico
const SecaoGrafico = memo(({ data, lojaDescricao, isMobile }) => {
  if (!data || data.length === 0) return <div>Nenhuma seção.</div>;

  const colors = generateUniqueColors(data.length);
  const chartData = {
    labels: data.map(s => s.descricao),
    datasets: [{
      label: '% Atingido da Meta',
      data: data.map(s => s.atingido ?? 0),
      backgroundColor: colors,
      borderColor: colors,
      borderWidth: 1,
      secaoData: data
    }]
  };

  const options = {
    responsive: true,
    indexAxis: 'y',
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, labels: { font: { size: isMobile ? 10 : 12 } } },
      title: { display: true, text: `% Atingido da Meta por Seção - ${lojaDescricao}`, font: { size: isMobile ? 14 : 16 } },
      tooltip: {
        callbacks: {
          title: (items) => items && items[0] ? `Seção: ${items[0].label}` : '',
          label: (ctx) => {
            // ctx - single tooltip item
            const secaoData = ctx.dataset.secaoData ? ctx.dataset.secaoData[ctx.dataIndex] : null;
            if (!secaoData) return `${ctx.dataset.label}: ${ctx.raw}`;
            const arr = [
              `Valor Venda: ${formatCurrency(secaoData.vl_vda)}`,
              `Meta: ${formatCurrency(secaoData.vlr_meta_dia)}`,
              `Margem: ${(secaoData.margem?? 0).toFixed(2)}%`,
              `% Atingido: ${(secaoData.atingido ?? 0).toFixed(2)}%`,
              `% Margem: ${secaoData.margem ? secaoData.margem.toFixed(2) + '%' : 'N/A'}`
            ];
            return arr;
          }
        }
      },
      datalabels: { color: '#000', font: { weight: 'bold', size: isMobile ? 8 : 10 }, anchor: 'end', align: 'right', formatter: (v) => `${(v ?? 0).toFixed(1)}%` }
    },
    scales: {
      x: { beginAtZero: true, title: { display: true, text: 'Percentual (%)' }, ticks: { callback: (val) => val + '%' } },
      y: { title: { display: isMobile, text: 'Seção' } }
    }
  };

  const dynamicHeight = Math.max(200, data.length * 28);
  return (
    <div className="secao-chart-container mt-3" style={{ height: `${dynamicHeight}px` }}>
      <MemoizedBar options={options} data={chartData} />
    </div>
  );
});


const FlashVendas = () => {
  const [vendas, setVendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Aguarde... Consultando registros');
  const [error, setError] = useState(null);
  const [timerSeconds, setTimerSeconds] = useState(60);
  const [expandedLojas, setExpandedLojas] = useState([]);
  const [secoesData, setSecoesData] = useState({});
  const [secoesLoading, setSecoesLoading] = useState({});
  const [showDetails, setShowDetails] = useState(false);
  const [detailsData, setDetailsData] = useState(null);

  const isMobile = window.innerWidth <= 768;
  const navigate = useNavigate();

  const chartRef = useRef(null);
  const intervalRef = useRef(null);
  const isInteracting = useRef(false);
  const scrollPosition = useRef(0);
  const expandedLojasRef = useRef([]);

  // mantém ref sincronizada
  useEffect(() => { expandedLojasRef.current = expandedLojas; }, [expandedLojas]);

  useEffect(() => {
    const chapa = localStorage.getItem("chapa");
    const permiteVendas = localStorage.getItem("permiteVendas");
    if (!chapa) {
      navigate("/arbor/login");
    } else if (permiteVendas !== "S") {
      Swal.fire({ icon: 'error', title: 'Acesso Negado', text: 'Não é permitido acessar as vendas.', confirmButtonText: 'OK' })
        .then(() => navigate("/arbor/home"));
    }
  }, [navigate]);

  const fetchVendas = async (showLoading = true) => {
    const permiteVendas = localStorage.getItem("permiteVendas");
    if (permiteVendas !== "S") return;

    try {
      if (showLoading) { setLoading(true); setLoadingMessage('Aguarde... Consultando registros'); }
      const chapa = localStorage.getItem("chapa");
      const res = await api.get('/venda/flashvenda', { params: { usuario: chapa } });
      setVendas(res.data || []);
      setError(null);
    } catch (err) {
      console.error('Erro ao buscar dados de vendas:', err);
      if (showLoading) {
        Swal.fire('Erro', 'Não foi possível carregar os dados de vendas.', 'error');
        setError('Não foi possível carregar os dados de vendas.');
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const fetchSecoes = async (loja) => {
    setSecoesLoading(prev => ({ ...prev, [loja]: true }));
    try {
      const chapa = localStorage.getItem("chapa");
      const res = await api.get('/venda/flashsecao', { params: { loja: loja, usuario: chapa } });
      setSecoesData(prev => ({ ...prev, [loja]: res.data || [] }));
    } catch (err) {
      console.error('Erro ao buscar dados de seções:', err);
      Swal.fire('Erro', `Não foi possível carregar os dados de seções para a loja ${loja}.`, 'error');
      setSecoesData(prev => ({ ...prev, [loja]: [] }));
    } finally {
      setSecoesLoading(prev => ({ ...prev, [loja]: false }));
    }
  };



  const startTimer = () => {
    clearInterval(intervalRef.current);
    setTimerSeconds(60);
    intervalRef.current = setInterval(() => {
      setTimerSeconds(prev => {
        if (prev <= 1) {
          if (!isInteracting.current) {
            scrollPosition.current = window.scrollY;
            fetchVendas(false); // sem loading global
            expandedLojasRef.current.forEach(loja => fetchSecoes(loja));
          }
          return 60;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // roda apenas no mount (corrigido)
  useEffect(() => {
    fetchVendas();
    startTimer();
    return () => clearInterval(intervalRef.current);
  }, []);

  useEffect(() => {
    if (scrollPosition.current !== 0) {
      window.scrollTo(0, scrollPosition.current);
      scrollPosition.current = 0;
    }
  }, [vendas]);

  useEffect(() => {
    const onInteract = () => { isInteracting.current = true; };
    const onNoInteract = () => { isInteracting.current = false; };
    window.addEventListener('mouseover', onInteract);
    window.addEventListener('focus', onInteract);
    window.addEventListener('mouseout', onNoInteract);
    window.addEventListener('blur', onNoInteract);
    return () => {
      window.removeEventListener('mouseover', onInteract);
      window.removeEventListener('focus', onInteract);
      window.removeEventListener('mouseout', onNoInteract);
      window.removeEventListener('blur', onNoInteract);
    };
  }, []);

  const fetchSecoesTotal = async () => {
    setSecoesLoading(prev => ({ ...prev, Emp: true }));
    try {
      const chapa = localStorage.getItem("chapa");
      const res = await api.get('/venda/flashsecaototal', {
        params: { usuario: chapa }
      });
      setSecoesData(prev => ({ ...prev, Emp: res.data || [] }));
    } catch (err) {
      console.error("Erro ao buscar seções totais:", err);
      Swal.fire("Erro", "Não foi possível carregar as seções do total da empresa.", "error");
      setSecoesData(prev => ({ ...prev, Emp: [] }));
    } finally {
      setSecoesLoading(prev => ({ ...prev, Emp: false }));
    }
  };

  const handleLojaClick = (loja) => {
    const open = expandedLojas.includes(loja);

    if (open) {
      // fechar → não carrega nada
      setExpandedLojas(prev => prev.filter(l => l !== loja));
      return;
    }

    // abrir → carrega as seções
    setExpandedLojas(prev => [...prev, loja]);

    if (loja === "Emp") {
      fetchSecoesTotal(); // GET diferente
    } else {
      fetchSecoes(loja);  // GET normal
    }

    startTimer();
    scrollPosition.current = window.scrollY;
  };

  const handleChartClick = (event) => {
    if (!chartRef.current) return;
    const chartInstance = chartRef.current;
    const active = chartInstance.getElementsAtEventForMode
      ? chartInstance.getElementsAtEventForMode(event, 'nearest', { intersect: true }, false)
      : [];
    if (active.length > 0) {
      const { index } = active[0];
      const clicked = dadosComTotal[index];
      setDetailsData({
        descricao: clicked.descricao,
        vl_vda: formatCurrency(clicked.vl_vda),
        vlr_meta_dia: formatCurrency(clicked.vlr_meta_dia),
        atingido: (clicked.atingido ?? 0).toFixed(2),
        margem: 'N/A',
        vlrvendapromoc: formatCurrency(clicked.vlrvendapromoc),
        perc_promocional: percPromoPorLinha(clicked).toFixed(2)
      });
      setShowDetails(true);
    }
  };

  const isExpanded = (loja) => expandedLojas.includes(loja);

  // cálculos
  const totalVenda = vendas.reduce((s, v) => s + (v.vl_vda ?? 0), 0);
  const totalMeta = vendas.reduce((s, v) => s + (v.vlr_meta_dia ?? 0), 0);
  const totalVendaPromoc = vendas.reduce((s, v) => s + (v.vlrvendapromoc ?? 0), 0);
  const totalLucro = vendas.reduce((s, v) => s + (v.vlrlucro ?? 0), 0);
  const totalAtingido = totalMeta > 0 ? (totalVenda / totalMeta) * 100 : 0;
  const percPromoPorLinha = (v) => v.vl_vda > 0 ? (v.vlrvendapromoc / v.vl_vda) * 100 : 0;

  const dadosComTotal = [
    ...vendas,
    { loja: 'Emp', descricao: 'Empresa', vl_vda: totalVenda, vlr_meta_dia: totalMeta, atingido: totalAtingido, vlrvendapromoc: totalVendaPromoc, vlrlucro: totalLucro }
  ];

  // gráfico unificado (lojas)
  const unifiedChartData = {
    labels: dadosComTotal.map(v => v.descricao),
    datasets: [
      {
        label: '% Atingido da Meta',
        data: dadosComTotal.map(v => v.atingido ?? 0),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      },
      {
        label: '% Promocional',
        data: dadosComTotal.map(v => percPromoPorLinha(v)),
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1
      }
    ]
  };

  const unifiedChartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: '% Atingido da Meta e Venda Promocional (%)' },
      tooltip: {
        callbacks: {
          title: (items) => {
            if (!items || items.length === 0) return '';
            const idx = items[0].dataIndex;
            return `Loja: ${dadosComTotal[idx].descricao}`;
          },
          label: (ctx) => {
            const datasetLabel = ctx.dataset.label;
            const idx = ctx.dataIndex;
            const data = dadosComTotal[idx];
            if (datasetLabel === '% Atingido da Meta') {
              return [
                `Venda: ${formatCurrency(data.vl_vda)}`,
                `Meta: ${formatCurrency(data.vlr_meta_dia)}`,
                `% Atingido: ${(data.atingido ?? 0).toFixed(2)}%`
              ];
            }
            if (datasetLabel === '% Promocional') {
              return `% Promocional: ${percPromoPorLinha(data).toFixed(2)}%`;
            }
            return `${datasetLabel}: ${ctx.raw}`;
          },
          footer: (items) => {
            if (!items || items.length === 0) return '';
            const idx = items[0].dataIndex;
            const data = dadosComTotal[idx];
            return `Margem: ${((data.vlrlucro*100/data.vl_vda) ?? 0).toFixed(2)}%`;
          }
        }
      },
      datalabels: { color: '#000', font: { weight: 'bold', size: isMobile ? 8 : 10 }, anchor: 'end', align: 'top', formatter: (v) => `${(v ?? 0).toFixed(1)}%`, rotation: -90 }
    },
    scales: {
      x: { stacked: false, title: { display: true, text: 'Lojas' } },
      y: { beginAtZero: true, stacked: false, title: { display: true, text: 'Percentual (%)' }, ticks: { callback: (val) => val + '%' } }
    }
  };

  const doughnutData = (percent, color) => ({
    labels: ['Percentual', 'Restante'],
    datasets: [
      { data: [Math.max(0, Math.min(100, percent)), 100 - Math.max(0, Math.min(100, percent))], backgroundColor: [color, 'rgba(220,220,220,0.35)'], borderWidth: 1 }
    ]
  });
  const doughnutOptions = { responsive: true, plugins: { legend: { display: false }, tooltip: { enabled: false }, datalabels: { display: false } }, cutout: '70%' };

  return (
    <div>
      <Menu modulo="ARBOR" />
      <h3 className="text-center"><br /></h3>

      <LoadingModal show={loading} message={loadingMessage} />
      <FloatingClock seconds={timerSeconds} />

      {!loading && !error && (
        <div className="container-fluid mt-4">
          <div className="chart-container">
            <MemoizedBar ref={chartRef} options={unifiedChartOptions} data={unifiedChartData} onClick={handleChartClick} />
          </div>

          {!isMobile && (
            <div className="table-responsive">
              <table className="table table-striped table-bordered mt-4">
                <thead className="thead-dark">
                  <tr>
                    <th>Descrição</th>
                    <th className="text-end">Valor Venda</th>
                    <th className="text-end">Meta do Dia</th>
                    <th className="text-end">Margem</th>
                    <th className="text-end">% Atingido da Meta</th>
                    <th className="text-end">Venda Promocional</th>
                    <th className="text-end">% Promocional</th>
                    <th>Seções</th>
                  </tr>
                </thead>
                <tbody>
                  {dadosComTotal.map((v, idx) => (
                    <React.Fragment key={idx}>
                      <tr className={v.loja === 'Emp' ? 'fw-bold' : ''}>
                        <td>{v.descricao}</td>
                        <td className="text-end">{formatCurrency(v.vl_vda)}</td>
                        <td className="text-end">{formatCurrency(v.vlr_meta_dia)}</td>
                        <td className="text-end">{(v.vlrlucro*100/v.vl_vda ?? 0).toFixed(2)}%</td>
                        <td className="text-end">{(v.atingido ?? 0).toFixed(2)}%</td>
                        <td className="text-end">{formatCurrency(v.vlrvendapromoc)}</td>
                        <td className="text-end">{percPromoPorLinha(v).toFixed(2)}%</td>
                        <td>
                          {(
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleLojaClick(v.loja)}
                            >
                              {isExpanded(v.loja) ? 'Ocultar' : 'Ver Seções'}
                            </button>
                          )}
                        </td>
                      </tr>

                      {isExpanded(v.loja) && (
                        <tr>
                          <td colSpan="7">
                            {secoesLoading[v.loja] ? (
                              <div className="text-center py-3"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                            ) : (
                              <SecaoGrafico data={secoesData[v.loja]} lojaDescricao={v.descricao} isMobile={isMobile} />
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {isMobile && (
            <div className="mt-4">
              {dadosComTotal.map((v, idx) => {
                const percPromo = percPromoPorLinha(v);
                const percAting = v.atingido ?? 0;
                return (
                  <div key={idx} className="card mb-2">
                    <div className="card-body">
                      <h5 className="card-title text-center mb-3">{v.descricao}</h5>
                      <div style={{ display: 'flex', justifyContent: 'space-around', gap: 12 }}>
                        <div style={{ position: 'relative', width: 140, height: 140 }}>
                          <MemoizedDoughnut data={doughnutData(percAting, 'rgba(54, 162, 235, 0.6)')} options={doughnutOptions} />
                          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: '0.95rem', fontWeight: 'bold', textAlign: 'center' }}>
                            {percAting.toFixed(1)}%<br /><span style={{ fontWeight: 400 }}>Atingido</span>
                          </div>
                        </div>

                        <div style={{ position: 'relative', width: 140, height: 140 }}>
                          <MemoizedDoughnut data={doughnutData(percPromo, 'rgba(255, 99, 132, 0.6)')} options={doughnutOptions} />
                          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: '0.95rem', fontWeight: 'bold', textAlign: 'center' }}>
                            {percPromo.toFixed(1)}%<br /><span style={{ fontWeight: 400 }}>Promo</span>
                          </div>
                        </div>
                      </div>

                      <p className="card-text text-center mt-3 mb-0">
                        <strong>Valor Venda:</strong> {formatCurrency(v.vl_vda)}<br />
                        <strong>Meta do Dia:</strong> {formatCurrency(v.vlr_meta_dia)}<br />
                        <strong>Venda Promocional:</strong> {formatCurrency(v.vlrvendapromoc)}
                      </p>
                      <p className="card-text text-red mt-3 mb-0">
                      <strong>Margem</strong> {(v.vlrlucro*100/v.vl_vda ?? 0).toFixed(2)}%<br />
                      </p>
                      {(
                        <div className="text-center mt-3">
                          <button
                            className="btn btn-primary"
                            onClick={() => handleLojaClick(v.loja)}
                          >
                            {isExpanded(v.loja) ? 'Ocultar' : 'Seções'}
                          </button>
                        </div>
                      )}
                    </div>

                    {isExpanded(v.loja) && (
                      <div className="card-footer bg-light">
                        {secoesLoading[v.loja] ? (
                          <div className="text-center py-3"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                        ) : (
                          <SecaoGrafico data={secoesData[v.loja]} lojaDescricao={v.descricao} isMobile={isMobile} />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showDetails && detailsData && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog"><div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{detailsData.descricao}</h5>
              <button type="button" className="btn-close" onClick={() => setShowDetails(false)}></button>
            </div>
            <div className="modal-body">
              <p><strong>Valor Venda:</strong> {detailsData.vl_vda}</p>
              <p><strong>Meta do Dia:</strong> {detailsData.vlr_meta_dia}</p>
              <p><strong>% Atingido:</strong> {detailsData.atingido}%</p>
              <p><strong>% Margem:</strong> {detailsData.margem}%</p>
              <p><strong>Venda Promocional:</strong> {detailsData.vlrvendapromoc}</p>
              <p><strong>% Promocional:</strong> {detailsData.perc_promocional}%</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDetails(false)}>Fechar</button>
            </div>
          </div></div>
        </div>
      )}

      {error && (<div className="container-fluid mt-4 text-center"><div className="alert alert-danger">Erro ao carregar os dados: {error}</div></div>)}

      <Footer className="footer navbar-fixed-bottom" />
    </div>
  );
};

export default FlashVendas;
